// react imports
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { InputNumber, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import axios from "axios";

// common imports
import { API_URL } from "common/constants/config";

import CategoryHeader from "./CategoryHeader";
import "./css/Pagination.css";
import "./css/Table.css";
import { VEHICLE_TYPE_NAMES } from "./constants/vehicle";

function Vehicles() {
  type VehicleType = "gasoline" | "diesel" | "hybrid" | "motorcycle";
  interface FuelTypes {
    gasoline: VehicleBase;
    diesel: VehicleBase;
    hybrid: VehicleBase;
  }

  interface VehicleBase {
    litresPerHundredKm: number;
    litresPerKm: number;
  }

  interface VehicleInputs {
    car: {
      [fuelType: string]: VehicleBase;
    };
    motorcycle: Pick<FuelTypes, "gasoline">;
  }

  interface VehicleData extends VehicleBase {
    key: string;
    vehicleType: string;
  }

  interface VehicleBaseResponse extends VehicleBase {
    co2EmissionPerLitre: number;
  }

  interface VehicleResponse {
    car: {
      [fuelType: string]: VehicleBaseResponse;
    };
    motorcycle: {
      gasoline: VehicleBaseResponse;
    };
  }

  const [vehicles, setVehicles] = useState<VehicleData[]>([]);
  const [vehicleResponse, setVehicleResponse] = useState<VehicleResponse>();
  const { control, handleSubmit } = useForm<VehicleInputs>();
  const [fetchAgain, setFetchAgain] = useState<Boolean>(false)
  const columns: ColumnsType<VehicleData> = [
    {
      title: "Tipo de vehículo",
      key: "vehicleType",
      dataIndex: "vehicleType",
      align: "center",
      width: 256,
    },
    {
      title: "l/100 Km",
      key: "litresPerHundredKm",
      dataIndex: "litresPerHundredKm",
      align: "center",
      width: 220,
      render: (value, record) => (
        <Controller
          name={
            record.key === "motorcycle"
              ? "motorcycle.gasoline.litresPerHundredKm"
              : `car.${record.key}.litresPerHundredKm`
          }
          defaultValue={value}
          control={control}
          render={({ field }) => <InputNumber {...field} />}
        />
      ),
    },
    {
      title: "l/Km",
      key: "litresPerKm",
      dataIndex: "litresPerKm",
      align: "center",
      width: 219,
      render: (value, record) => (
        <Controller
          name={
            record.key === "motorcycle"
              ? "motorcycle.gasoline.litresPerKm"
              : `car.${record.key}.litresPerKm`
          }
          defaultValue={value}
          control={control}
          render={({ field }) => <InputNumber readOnly={true} value={record.litresPerHundredKm/100} />}
        />
      ),
    },
  ];

  async function onSubmit(values: VehicleInputs) {
    setFetchAgain(false)
    const { car, motorcycle } = vehicleResponse as VehicleResponse;
    for (const fuel in car) {
      Object.assign(car[fuel], values.car[fuel]);
    }
    Object.assign(motorcycle.gasoline, values.motorcycle.gasoline);

    const payload = {
      vehicle: { car, motorcycle },
    };
    await axios.put("/admin/calculator/transport", payload, {
      baseURL: API_URL,
    });
    setFetchAgain(true)
  }

  async function fetchVehicles() {
    const {
      data: {
        transport: { vehicle: vehicleValues },
      },
    } = await axios.get(`${API_URL}/admin/calculator/transport`);

    const mappedVehicles = Object.entries<FuelTypes>(vehicleValues)
      .map(([name, values]) => {
        if (name === "car") {
          return Object.entries(values).map(([fuelType, carValues]) => {
            return {
              key: fuelType,
              vehicleType: VEHICLE_TYPE_NAMES[fuelType as VehicleType],
              litresPerHundredKm: carValues.litresPerHundredKm as number,
              litresPerKm: carValues.litresPerKm as number,
            };
          });
        }

        return {
          key: name,
          vehicleType: VEHICLE_TYPE_NAMES[name as VehicleType],
          litresPerHundredKm: values.gasoline.litresPerHundredKm,
          litresPerKm: values.gasoline.litresPerKm,
        };
      })
      .flat();

    setVehicleResponse(vehicleValues);
    setVehicles(mappedVehicles);
  }

  useEffect(() => {
    fetchVehicles();
  }, [fetchAgain]);

  return (
    <>
      <CategoryHeader title="Vehículos" formName="vehicles" />
      <form id="vehicles" onSubmit={handleSubmit(onSubmit)}>
        <Table columns={columns} dataSource={vehicles} pagination={false} />
      </form>
    </>
  );
}

export default Vehicles;
